import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./DropGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue, QuizQueue } from "../Quiz/quizQueue.ts";
import { readBestScore, recordScore } from "../Quiz/bestScore.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { DropGameResult, DropGameRun } from "./DropGameRun.tsx";
import { DropGameSummary } from "./DropGameSummary.tsx";
import { dropBestScoreKey } from "./dropGame.ts";
import { gameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface DropGamePageProps {
    slug: string;
    operation: Operations;
}

interface Summary {
    result: DropGameResult;
    best: number | null;
    isNewBest: boolean;
}

/**
 * The falling-facts game (ADR 0053): a keyword landing page around a game on
 * the quiz's queue, marking and stores. A quiz route carrying `game`, which is
 * how App.js tells it from a plain quiz (§5). Its `quiz` is only the operation
 * the fact picker opens on (ADR 0098 §1).
 */
export function DropGamePage({ slug, operation }: DropGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const [queue, setQueue] = useState<QuizQueue | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [missesVersion, setMissesVersion] = useState(0);

    const ready = canBuildDeck(settings);
    const playing = queue !== null && summary === null;
    // The calmer alternative the accessibility section of the ADR points to: the
    // plain quiz for the one operation chosen, else (as on Soccer) the page's own.
    const chosen = operationsOf(settings);
    const quizOperation = chosen.length === 1 ? chosen[0] : operation;
    const plainQuiz = quizRoutes().find((route) => route.quiz === quizOperation && !route.game);
    // Captured at the start, so a result is keyed by what was actually played.
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);

    function startGame() {
        // Built here from settings as they stand, never during render: the
        // generator is non-deterministic (ADR 0001 §4).
        setQueue(createQuizQueue(settings));
        setPlayedOperations(operationsOf(settings));
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function changeFacts() {
        setQueue(null);
        setSummary(null);
    }

    const handleFinish = useCallback(
        (result: DropGameResult) => {
            const key = dropBestScoreKey(playedOperations);
            const previous = readBestScore(key);
            const best = recordScore(key, result.cleared);
            result.landed.forEach((fact) => recordMiss(factKey(fact.front)));
            result.clearedFronts.forEach((front) => recordCorrect(factKey(front)));
            setMissesVersion((v) => v + 1);
            // After the stores, as on the quiz: analytics never costs a best score.
            track("quiz_completed", gameEventParams(playedOperations, result));
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest: previous !== null ? result.cleared > previous : result.cleared > 0,
            });
        },
        [playedOperations]
    );

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool">
                {playing ? (
                    <DropGameRun key={runId} queue={queue!} onFinish={handleFinish} />
                ) : summary && ready ? (
                    <DropGameSummary
                        result={summary.result}
                        best={summary.best}
                        isNewBest={summary.isNewBest}
                        onPlayAgain={startGame}
                        onChangeFacts={changeFacts}
                    />
                ) : (
                    <div className="dropIntro">
                        <p>{t("flashCards.gameHowToPlay")}</p>
                        <p className="quizModePrompt">{t("flashCards.soccerWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.soccerWhichFacts")} />
                        {ready ? (
                            <button type="button" className="dropStart" onClick={startGame}>
                                {t("flashCards.gameStart")}
                            </button>
                        ) : (
                            <p className="quizEmpty">{t("flashCards.gameEmptyNote")}</p>
                        )}
                    </div>
                )}

                {!playing && plainQuiz && (
                    <p className="dropCalmer">
                        {t("flashCards.gameCalmerPrefix")}
                        <a href={plainQuiz.slug}>{t("flashCards.gameCalmerLink")}</a>
                        {t("flashCards.gameCalmerSuffix")}
                    </p>
                )}

                {!playing && <MissedFactsPractice refreshKey={missesVersion} />}

                {!playing && (
                    <details className="quizSettings">
                        <summary>{t("flashCards.changeWhatToPractise")}</summary>
                        <div className="valueRangeContainer">
                            <ValueRange RangeSettings={0} />
                            <ValueRange RangeSettings={1} />
                        </div>
                    </details>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default DropGamePage;
